import { inject } from '@angular/core';
import {
  boolToString,
  formatDate,
  formatNumber,
  MiqMoment,
  simpleValueValidation,
  traverseTree,
  MIQQLParser,
  DASH,
  EMPTY_STRING,
} from '@miq/utils';
import { Column, hasReadPath, InputType, RawColumn } from '@miq/catalog';
import { digger } from '@cbre/digger';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';

const COMPANY_CONTACT_PATH = 'company_contact_role_addresses';

let cachedLogger: CbreLogger | null = null;
const getLogger = (): CbreLogger => {
  if (cachedLogger) {
    return cachedLogger;
  }
  try {
    cachedLogger = inject(CbreLoggerToken);
  } catch {
    cachedLogger = {
      info: () => {},
      debug: () => {},
      warn: () => {},
      error: () => {},
    };
  }
  return cachedLogger;
};

export const getUserViewOptions = () => {
  const userProfileSettings = localStorage.getItem('USER_SETTINGS')
    ? JSON.parse(localStorage.getItem('USER_SETTINGS'))
    : null;

  if (userProfileSettings?.userSettings?.viewOptions != null) {
    const { userSettings } = userProfileSettings;
    const { viewOptions } = userSettings;
    return viewOptions;
  } else {
    return { rents: null, globalDateFormat: null };
  }
};

export const getGlobalDateDisplayFormat = (key: string = 'code', extended = false): string => {
  const userPreferences = getUserViewOptions();
  const { globalDateFormat } = userPreferences;
  const dateOpt = !!globalDateFormat && globalDateFormat.length ? globalDateFormat[0] : null;
  if (!!dateOpt) {
    return (extended && `${dateOpt[key]} h:mm:ss a`) || dateOpt[key];
  }
  return key !== 'code'
    ? (extended && 'MM/DD/YYYY h:mm:ss a') || 'MM/DD/YYYY'
    : (extended && 'M/d/yyyy h:mm:ss a') || 'M/d/yyyy';
};

export const dateSanitizer = (value: string, toUtc?: boolean, extended?: boolean) => {
  const isValid = value != null && value !== '';
  const extendedFormat = extended ? 'h:mm:ss a' : '';
  let result = '';

  if (isValid) {
    const formattedDate = MiqMoment.formatFromInputFormat(
      value,
      MiqMoment.utcInputFormat,
      `MM/DD/YYYY ${extendedFormat}`,
      toUtc
    );
    const outputFormat = getGlobalDateDisplayFormat(undefined, extended);
    result = formatDate(formattedDate, outputFormat, 'en-US') as string;
  }
  return result;
};

const SANITIZER_MAP_BY_DATA_TYPE = {
  boolean: (value, opts?) => (opts.values ? value : boolToString(value)),
  integer: (value, opts?) =>
    formatNumber(value, 'en-US', !!opts.options && !!opts.options.format ? opts.options.format : undefined),
  numeric: (value, opts?) =>
    formatNumber(value, 'en-US', !!opts.options && !!opts.options.format ? opts.options.format : undefined),
};

const SANITIZER_MAP_BY_INPUT_TYPE = {
  Date: (value, opts?) => dateSanitizer(value, opts.dataType !== 'timestamp with time zone'),
};

const self = s => s;

export const sanitizeValue = (value, { dataType, inputType, values, options }: any) => {
  const sanitizer = SANITIZER_MAP_BY_INPUT_TYPE[inputType] || SANITIZER_MAP_BY_DATA_TYPE[dataType] || self;
  return sanitizer(value, { dataType, values, options });
};

export const getLovValueKey = <T extends RawColumn = Column>(column: Pick<T, 'lookupValueKey'>) =>
  column && column.lookupValueKey ? column.lookupValueKey : 'key';

export const getValueByColum = (column, record) => {
  const { field = '', condition } = column;

  let extractedData;
  if (!column.path && simpleValueValidation(condition)) {
    let parsedCondition;
    try {
      parsedCondition = MIQQLParser.parse(condition);
      extractedData = digger(record, field, parsedCondition);
    } catch {
      if (parsedCondition != null) {
        getLogger().error(`Something went wrong while extracting data from ${field} with condition ${condition}`);
        extractedData = digger(record, field);
      } else {
        getLogger().error(
          // tslint:disable-next-line:max-line-length
          `Something went wrong while parsing the catalog condition for\n - field: '${column.header}'\n - condition: '${condition}'`
        );
      }
    }
  } else {
    // prevent failure of unimplemented ColumnFactory
    if (!column.getReadPath) {
      column = Object.assign(column, hasReadPath());
    }
    extractedData = digger(record, column.getReadPath());
  }
  return extractedData;
};

export const extractData = (data: any, column: any) => {
  let extractedData: any;
  // prevent failure of unimplemented ColumnFactory
  if (!column.getReadPath) {
    column = Object.assign(column, hasReadPath());
  }
  extractedData = digger(data, column.getReadPath());
  return extractedData;
};

export const getValueFromLOVList = (
  keyList: string[],
  column: Partial<Column>,
  filterDuplicates: boolean,
  lovValueFormatter: Function
) => {
  // Get the Lov key could be key or value depending on the Column
  const dataKey = getLovValueKey(column);
  // Get the list of the values from the LOV options
  return filterDuplicates
    ? getUniqueValuesFromLOVList(keyList, dataKey, column, lovValueFormatter)
    : getAllValuesFromLOVList(keyList, dataKey, column, lovValueFormatter, DASH);
};

const getUniqueValuesFromLOVList = (
  keyList: any[],
  dataKey: string,
  column: Partial<Column>,
  lovValueFormatter: Function
) => {
  const extractedDataMap = Object.assign({}, ...keyList.map(val => ({ [val]: true })));

  if (column.inputType === InputType.NAICS) {
    const result = traverseTree(column.values, keyList, { dataKey });
    if (result.length > 0) {
      return result.map(val => (lovValueFormatter ? lovValueFormatter(val) : val.value));
    }
    return [];
  } else {
    return column.values
      .filter(val => val[dataKey] != null && extractedDataMap[val[dataKey]])
      .map(val => (lovValueFormatter ? lovValueFormatter(val) : val.value));
  }
};

const getAllValuesFromLOVList = (
  keyList: any[],
  dataKey: string,
  column: Partial<Column>,
  lovValueFormatter: Function,
  valueWhenEmpty = EMPTY_STRING
) => {
  // Make a mapping of the available LOVs
  const availableLOVsMap = Object.assign(
    {},
    ...(column.values ?? []).map(lovItem => ({ [lovItem[dataKey]]: lovItem }))
  );

  return keyList.map(keyItem =>
    lovValueFormatter
      ? lovValueFormatter(availableLOVsMap[keyItem])
      : (availableLOVsMap[keyItem]?.value ?? valueWhenEmpty)
  );
};

export const getFormattedValue = (column, recordValue, filterDuplicates) => {
  const _valueWhenEmpty = column?.replaceEmptyWith ?? EMPTY_STRING;

  if (Array.isArray(recordValue)) {
    if (filterDuplicates) {
      const result = Array.from(
        new Set(recordValue.filter(value => value != null && value !== '').map(value => sanitizeValue(value, column)))
      );
      // Special case for company_contact_role_addresses not to sort the values and return as per sort order
      return (
        column?.field?.includes(COMPANY_CONTACT_PATH)
          ? result
          : result.sort((a, b) => {
              if (typeof a === 'number' && typeof b === 'number') {
                return a - b;
              }
              if (a instanceof Date && b instanceof Date) {
                return a.getTime() - b.getTime();
              }
              return String(a).localeCompare(String(b));
            })
      ).join(', ');
    }
    return Array.from(recordValue, value => sanitizeValue(value, column) ?? _valueWhenEmpty).join(', ');
  }
  return sanitizeValue(recordValue, column).toString().trim() || _valueWhenEmpty;
};
